import type { ResourceKeyFactory, ResourceScope } from "./resourceKeys";
import { useQuery } from "@tanstack/vue-query";
import { paramsToKey } from "@verbleif/lib/src/composables/Query/hydraInfinite";
import { queryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { computed } from "vue";
import { getAll } from "../Api/api.helpers";
import { useResourceScope } from "./resourceKeys";

export function fetchAllPages<T>(options: {
  url: string
  initialParams?: URLSearchParams
  perPage?: number
  signal?: AbortSignal
  _retry?: boolean
  concurrency?: number
  onPage?: (items: T[]) => void
}): Promise<T[]> {
  return getAll<T>(options);
}

export function useResourceAll<T>(
  keys: ResourceKeyFactory,
  url: string,
  buildParams?: (params: URLSearchParams, scope: ResourceScope) => void,
) {
  const scope = useResourceScope();
  const enabled = computed(() => scope.value.location != null || scope.value.client != null);

  const query = useQuery(computed(() => ({
    queryKey: keys.all(scope.value),
    enabled: enabled.value,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const params = new URLSearchParams();
      buildParams?.(params, scope.value);
      return getAll<T>({ url, initialParams: params, signal });
    },
  })));

  return {
    items: computed(() => query.data.value ?? []),
    loading: query.isPending,
    refetch: query.refetch,
    queryKey: computed(() => keys.all(scope.value)),
  };
}

export function ensureResourceAll<T>(
  keys: ResourceKeyFactory,
  url: string,
  scope: ResourceScope,
  buildParams?: (params: URLSearchParams) => void,
): Promise<T[]> {
  return queryClient.fetchQuery({
    queryKey: keys.all(scope),
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const params = new URLSearchParams();
      buildParams?.(params);
      return getAll<T>({ url, initialParams: params, signal });
    },
  }).then(toArray);
}

// The catalog stores some resources (users, userGroups) as an IRI record under the same "all" key.
function toArray<T>(data: T[] | Record<string, T>): T[] {
  return Array.isArray(data) ? data : Object.values(data ?? {});
}

export function ensureResourceList<T = any>(
  keys: ResourceKeyFactory,
  url: string,
  scope: ResourceScope,
  buildParams?: (params: URLSearchParams) => void,
): Promise<T[]> {
  const params = new URLSearchParams();
  buildParams?.(params);
  return queryClient.fetchQuery({
    queryKey: [...keys.list(scope), paramsToKey(params)],
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      return getAll<T>({ url, initialParams: params, signal });
    },
  });
}

export function ensureLocationResourceAll<T>(
  keys: ResourceKeyFactory,
  url: string,
  locationIri: string,
  currentScope: ResourceScope,
): Promise<T[]> {
  const reuseCatalog = currentScope.location === locationIri;
  const scope = reuseCatalog ? currentScope : { location: locationIri, client: null };
  return ensureResourceAll<T>(keys, url, scope, (params) => {
    if (scope.location) {
      params.set("location", scope.location);
    }
    if (scope.client) {
      params.set("client", scope.client);
    }
    if (reuseCatalog) {
      params.set("active", "true");
    }
  });
}
